import type { useAuth } from "@clerk/react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@ndd/ui";
import { Lock, Sparkles } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import type { AppointmentNoteResponse } from "../../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../../shared/features";
import { useFeatureEnabled } from "@/lib/use-features";
import {
  draftEditorKey,
  enhanceFooterState,
  isDraftTabLocked,
  opensDraftTab,
  reEnhanceEnabled,
  autoEnhanceHintEnabled,
  reEnhanceOffered,
  reEnhanceRevision,
  type AppointmentWorkspaceTab,
} from "@/lib/appointment-workspace-state";
import { soapDraftDeliveryFromSnapshot } from "@/lib/soap-draft-delivery";
import { AppointmentDraftEditor } from "./appointment-draft-editor";
import { AppointmentDraftReadOnly } from "./appointment-draft-read-only";
import { AppointmentNoteEditor } from "./appointment-note-editor";
import { AppointmentNoteReadOnly } from "./appointment-note-read-only";
import { EnhanceFooter } from "./enhance-footer";
import { NewerHealthieTranscriptProvider } from "./draft-transcript-notice";
import { usePasteTranscriptAction } from "./paste-transcript-dialog";
import { useReEnhanceAction } from "./re-enhance-dialog";
import {
  useAppointmentDocumentation,
  useReadOnlyAppointmentDocumentation,
} from "./use-appointment-documentation";
import {
  TAB_ICON_CLASSIC_ONLY,
  TAB_LIST,
  TAB_ROW,
  TAB_TRIGGER,
} from "./workspace-look";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * The two-tab documentation surface: Appointment note, then the Enhanced SOAP
 * draft once an Enhancement exists. Both editors stay mounted across tab
 * switches so autosave and the unsaved-text guard keep working out of view;
 * the inactive one is hidden, not unmounted.
 */
export function AppointmentWorkspace({
  appointmentId,
  note,
  getToken,
  controlsEnd,
}: {
  appointmentId: string;
  note: AppointmentNoteResponse;
  getToken: ClerkGetToken;
  /** Right end of the controls row: the Previous SOAP notes toggle. */
  controlsEnd?: ReactNode;
}): ReactElement {
  const documentation = useAppointmentDocumentation({ appointmentId, getToken });
  const { generation } = documentation.ui;
  const locked = isDraftTabLocked(generation);
  const { tab, setTab } = useWorkspaceTab(generation.phase, locked);
  const paste = usePasteTranscriptAction(
    documentation.generateFromPastedTranscript,
    useFeatureEnabled(FEATURE_FLAGS.manualSoapGeneration)
  );
  const reEnhance = useWorkspaceReEnhance(documentation);

  return (
    <NewerHealthieTranscriptProvider
      value={documentation.ui.snapshot?.newerHealthieTranscript === true}
    >
      {paste.dialog}
      {reEnhance.dialog}
      <WorkspaceTabs
        tab={tab}
        setTab={setTab}
        locked={locked}
        controlsEnd={controlsEnd}
        explainLocked
        animateUnlock
        note={
          <AppointmentNoteEditor
            key={appointmentId}
            appointmentId={appointmentId}
            initial={note}
            getToken={getToken}
            footer={workspaceEnhanceFooter(
              documentation,
              { reEnhance, paste, autoEnhanceHint: useAutoEnhanceHint() },
              () => setTab("draft")
            )}
          />
        }
        draft={
          locked
            ? null
            : ownedDraftEditor(
                appointmentId,
                documentation,
                getToken,
                reEnhance.onShownRevision
              )
        }
      />
    </NewerHealthieTranscriptProvider>
  );
}

function workspaceEnhanceFooter(
  documentation: ReturnType<typeof useAppointmentDocumentation>,
  actions: {
    reEnhance: ReturnType<typeof useWorkspaceReEnhance>;
    paste: ReturnType<typeof usePasteTranscriptAction>;
    autoEnhanceHint: boolean;
  },
  onOpenDraft: () => void
): ReactElement {
  const { reEnhance, paste, autoEnhanceHint } = actions;

  return (
    <EnhanceFooter
      state={enhanceFooterState(documentation.ui, reEnhance.enabled)}
      reconnecting={documentation.ui.connection === "reconnecting"}
      onEnhance={documentation.enhance}
      onRefresh={() => void documentation.refresh()}
      onOpenDraft={onOpenDraft}
      {...paste.footerProps}
      {...reEnhance.footerProps}
      {...(autoEnhanceHint ? { autoEnhanceHint } : {})}
    />
  );
}

/**
 * NO-428: the ready-copy hint, shown only while `soap-enhancement` and
 * `soap-auto-enhance` are both on for the signed-in RD. This workspace mounts
 * only on the owner's editable Appointment, so the viewer is the owning RD.
 */
function useAutoEnhanceHint(): boolean {
  return autoEnhanceHintEnabled({
    soapEnhancement: useFeatureEnabled(FEATURE_FLAGS.soapEnhancement),
    soapAutoEnhance: useFeatureEnabled(FEATURE_FLAGS.soapAutoEnhance),
  });
}

/**
 * NO-785: the Re-enhance action, offered while `soap-enhancement` and
 * `soap-re-enhance` are both on (the route needs both) and the Draft was never
 * approved, against the head the Draft tab shows.
 */
function useWorkspaceReEnhance(
  documentation: ReturnType<typeof useAppointmentDocumentation>
): ReturnType<typeof useReEnhanceAction> & {
  enabled: boolean;
  onShownRevision: (revisionId: string | null) => void;
} {
  const enabled = reEnhanceEnabled({
    soapEnhancement: useFeatureEnabled(FEATURE_FLAGS.soapEnhancement),
    soapReEnhance: useFeatureEnabled(FEATURE_FLAGS.soapReEnhance),
  });
  // The revision the Draft editor has loaded, which may be older than the
  // polled head when another tab saved since: Re-enhance must name that one.
  const [shownRevisionId, setShownRevisionId] = useState<string | null>(null);
  const { generation, reEnhancement } = documentation.ui;
  const action = useReEnhanceAction(
    documentation.reEnhance,
    reEnhanceOffered(enabled, reEnhancement),
    reEnhanceRevision(shownRevisionId, generation)
  );

  return { ...action, enabled, onShownRevision: setShownRevisionId };
}

function ownedDraftEditor(
  appointmentId: string,
  documentation: ReturnType<typeof useAppointmentDocumentation>,
  getToken: ClerkGetToken,
  onShownRevision: (revisionId: string | null) => void
): ReactElement {
  return (
    <AppointmentDraftEditor
      onShownRevision={onShownRevision}
      key={draftEditorKey(documentation.ui)}
      appointmentId={appointmentId}
      reEnhancing={documentation.ui.reEnhancement?.state === "running"}
      generation={documentation.ui.generation}
      enhanceAction={documentation.ui.enhanceAction}
      delivery={soapDraftDeliveryFromSnapshot(documentation.ui.snapshot)}
      reconnecting={documentation.ui.connection === "reconnecting"}
      onRetry={documentation.enhance}
      onApproved={documentation.refresh}
      getToken={getToken}
    />
  );
}

/** The same two-tab workspace read side without any mutating lifecycle. */
export function ReadOnlyAppointmentWorkspace({
  appointmentId,
  rdId,
  note,
  getToken,
  controlsEnd,
}: {
  appointmentId: string;
  rdId: string;
  note: AppointmentNoteResponse;
  getToken: ClerkGetToken;
  controlsEnd?: ReactNode;
}): ReactElement {
  const documentation = useReadOnlyAppointmentDocumentation({
    appointmentId,
    rdId,
    getToken,
  });
  const generation = documentation.generation;
  const locked = isDraftTabLocked(generation);
  const { tab, setTab } = useWorkspaceTab(generation.phase, locked);

  return (
    <NewerHealthieTranscriptProvider value={documentation.newerHealthieTranscript}>
      <WorkspaceTabs
        tab={tab}
        setTab={setTab}
        locked={locked}
        controlsEnd={controlsEnd}
        note={
          <AppointmentNoteReadOnly
            key={`${appointmentId}:${rdId}`}
            document={note.document}
          />
        }
        draft={
          locked ? null : (
            <AppointmentDraftReadOnly
              appointmentId={appointmentId}
              rdId={rdId}
              generation={generation}
              delivery={documentation.writeback}
              documentationUnavailable={documentation.unavailable}
              getToken={getToken}
            />
          )
        }
      />
    </NewerHealthieTranscriptProvider>
  );
}

function useWorkspaceTab(
  generationPhase: Parameters<typeof opensDraftTab>[1],
  locked: boolean
): {
  tab: AppointmentWorkspaceTab;
  setTab: (tab: AppointmentWorkspaceTab) => void;
} {
  const [tab, setTab] = useState<AppointmentWorkspaceTab>("note");
  const previousPhase = useRef(generationPhase);
  useEffect(() => {
    if (opensDraftTab(previousPhase.current, generationPhase)) setTab("draft");

    previousPhase.current = generationPhase;
  }, [generationPhase]);
  useEffect(() => {
    if (locked) setTab("note");
  }, [locked]);

  return { tab, setTab };
}

function WorkspaceTabs({
  tab,
  setTab,
  locked,
  controlsEnd,
  explainLocked = false,
  animateUnlock = false,
  note,
  draft,
}: {
  tab: AppointmentWorkspaceTab;
  setTab: (tab: AppointmentWorkspaceTab) => void;
  locked: boolean;
  controlsEnd?: ReactNode;
  explainLocked?: boolean;
  animateUnlock?: boolean;
  note: ReactNode;
  draft: ReactNode;
}): ReactElement {
  return (
    <Tabs
      value={tab}
      onValueChange={(value) => setTab(value as AppointmentWorkspaceTab)}
      className="flex min-h-0 flex-1 flex-col"
    >
      <WorkspaceControls
        locked={locked}
        controlsEnd={controlsEnd}
        explainLocked={explainLocked}
        animateUnlock={animateUnlock}
      />
      <TabsContent value="note" forceMount className={tabContentClass}>
        {note}
      </TabsContent>
      {draft ? (
        <TabsContent value="draft" forceMount className={tabContentClass}>
          {draft}
        </TabsContent>
      ) : null}
    </Tabs>
  );
}

function WorkspaceControls({
  locked,
  controlsEnd,
  explainLocked,
  animateUnlock,
}: {
  locked: boolean;
  controlsEnd?: ReactNode;
  explainLocked: boolean;
  animateUnlock: boolean;
}): ReactElement {
  const trigger = <DraftTabTrigger locked={locked} animateUnlock={animateUnlock} />;
  const draftControl =
    locked && explainLocked ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex h-full" tabIndex={0}>
            {trigger}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="duration-150">
          Enhance your notes to unlock the draft
        </TooltipContent>
      </Tooltip>
    ) : (
      trigger
    );

  return (
    <div className={TAB_ROW}>
      <TabsList variant="underline" aria-label="Documentation" className={TAB_LIST}>
        <TabsTrigger value="note" className={TAB_TRIGGER}>
          Appointment note
        </TabsTrigger>
        {draftControl}
      </TabsList>
      {controlsEnd}
    </div>
  );
}

function DraftTabTrigger({
  locked,
  animateUnlock,
}: {
  locked: boolean;
  animateUnlock: boolean;
}): ReactElement {
  const unlockedClass = animateUnlock
    ? "text-ai animate-in zoom-in-50 duration-300 motion-reduce:animate-none"
    : "text-ai";

  return (
    <TabsTrigger
      value="draft"
      disabled={locked}
      className={cn(TAB_TRIGGER, locked && "text-subtle disabled:opacity-100")}
    >
      <Sparkles
        key={locked ? "locked" : "unlocked"}
        aria-hidden="true"
        className={cn(
          "size-3.5",
          locked ? "text-subtle" : unlockedClass,
          TAB_ICON_CLASSIC_ONLY
        )}
      />
      Enhanced SOAP draft
      {locked ? <Lock aria-hidden="true" className="size-3 text-subtle" /> : null}
    </TabsTrigger>
  );
}

// Incoming editor fades in over the outgoing one; no lateral shift.
const tabContentClass =
  "mt-3.5 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-150 motion-reduce:animate-none";
